export const newDraftKey = () => `new:${window.crypto.randomUUID()}`;
const clone = (value) => JSON.parse(JSON.stringify(value));
const equal = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const selected = (values) => values.filter((entry) => entry.selected).map((entry) => entry.id);
const normalizeRegister = (entry) => ({ ...entry, discountApproverUserId: entry.discountApproverUserId == null ? null : String(entry.discountApproverUserId) });

export const makeRegisterDraft = (key, index = 1) => ({
  key, name: `Caja ${index}`, maxOpenSales: 5, allowedProductFormatCodes: [],
  users: [], discountApproverUserId: null, isDeleted: false, isActive: true,
});

export const createConfigurationDraft = (data, firstRegisterKey = newDraftKey()) => ({
  branch: { branchName: data.branch?.branchName || '' },
  users: clone(data.users),
  registers: data.branch ? data.registers.map(normalizeRegister) : [makeRegisterDraft(firstRegisterKey)],
  registerless: data.registerless ? normalizeRegister(clone(data.registerless)) : null,
  taxIds: selected(data.taxes),
  categories: data.categories.map((entry) => ({ id: entry.id, isActive: Boolean(entry.selected) })),
  action: null,
});

export const setUserRegisterAccess = (draft, userKey, registerKey, enabled) => ({
  ...draft,
  registers: draft.registers.map((register) => register.key === registerKey ? {
    ...register,
    users: enabled ? [...new Set([...register.users, userKey])] : register.users.filter((key) => key !== userKey),
  } : register),
});

export const removeUserAccess = (draft, key, removeCompany = false) => ({
  ...draft,
  users: draft.users.map((user) => user.key === key ? {
    ...user, access: false, roleId: null, _listed: true, ...(removeCompany ? { action: 'removeCompany' } : {}),
  } : user),
  registers: draft.registers.map((register) => ({
    ...register, users: register.users.filter((ref) => ref !== key),
    discountApproverUserId: register.discountApproverUserId === key ? null : register.discountApproverUserId,
  })),
  registerless: draft.registerless ? {
    ...draft.registerless, users: draft.registerless.users.filter((ref) => ref !== key),
    discountApproverUserId: draft.registerless.discountApproverUserId === key ? null : draft.registerless.discountApproverUserId,
  } : null,
});

const fieldsChanged = (current, previous, fields) => Object.fromEntries(fields
  .filter((field) => !equal(current[field], previous?.[field]))
  .map((field) => [field, current[field]]));

export const buildConfigurationChanges = (draft, original, data) => {
  if (draft.action) return { action: draft.action };
  const changes = {};
  const branch = fieldsChanged(draft.branch, original.branch, ['branchName']);
  if (!data.branch || Object.keys(branch).length) changes.branch = data.branch ? branch : { ...draft.branch };
  const users = draft.users.flatMap((user) => {
    const before = original.users.find((entry) => entry.key === user.key);
    if (user.action) return [{ id: user.id, key: user.key, action: user.action }];
    const op = before ? fieldsChanged(user, before, ['name', 'access']) : { name: user.name, username: user.username, ...(user.password ? { password: user.password } : {}), ...(user.access ? { access: true } : {}) };
    if (!user.inherited && user.access && (!before || !before.access || user.roleId !== before.roleId)) op.roleId = Number(user.roleId);
    if (!Object.keys(op).length) return [];
    return [{ key: user.key, ...(user.id ? { id: user.id } : {}), ...op }];
  });
  if (users.length) changes.users = users;
  // Access revocation performs this cleanup transactionally on the server. Compare
  // against that result so it does not require unrelated register permissions.
  const revoked = new Set(users.filter((user) => user.access === false || user.action === 'removeCompany').map((user) => user.key));
  const afterRevocation = (entry) => entry && ({ ...entry,
    users: entry.users.filter((key) => !revoked.has(key)),
    discountApproverUserId: revoked.has(entry.discountApproverUserId) ? null : entry.discountApproverUserId,
  });
  const registers = draft.registers.flatMap((register) => {
    if (register.action) return [{ id: register.id, key: register.key, action: register.action }];
    const before = afterRevocation(original.registers.find((entry) => entry.key === register.key));
    const fields = ['name', 'maxOpenSales', 'allowedProductFormatCodes', 'discountApproverUserId'];
    if (data.permissions.assignRegisters && data.permissions.readUsers) fields.push('users');
    const op = fieldsChanged(register, register.id ? before : null, fields);
    if (!Object.keys(op).length) return [];
    return [{ key: register.key, ...(register.id ? { id: register.id } : {}), ...op }];
  });
  if (registers.length) changes.registers = registers;
  if (!equal(draft.taxIds, original.taxIds)) changes.taxIds = draft.taxIds;
  const categories = draft.categories.filter((entry) => original.categories.find((before) => before.id === entry.id)?.isActive !== entry.isActive);
  if (categories.length) changes.categories = categories;
  if (draft.registerless) {
    const op = fieldsChanged(draft.registerless, afterRevocation(original.registerless), ['isActive', 'allowedProductFormatCodes', 'discountApproverUserId', 'users', 'categoryIds', 'postSubmitDestination']);
    if (Object.keys(op).length) changes.registerless = op;
  }
  return changes;
};

export const validateConfigurationDraft = (draft, original, data) => {
  const errors = {};
  if (draft.action) return errors;
  if (!draft.branch.branchName.trim()) errors['branch.branchName'] = 'El nombre de la sucursal es obligatorio.';
  const changes = buildConfigurationChanges(draft, original, data);
  (changes.users || []).filter((user) => !user.action).forEach((op) => {
    const user = draft.users.find((entry) => entry.key === op.key);
    if (!user.name?.trim()) errors[`users.${user.key}.name`] = 'El nombre es obligatorio.';
    if (!user.id && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.username || '')) errors[`users.${user.key}.username`] = 'Ingresa un email válido.';
    if (op.roleId !== undefined && !op.roleId) errors[`users.${user.key}.roleId`] = 'Selecciona un rol para esta sucursal.';
  });
  const names = new Set();
  draft.registers.filter((register) => !register.isDeleted && register.action !== 'remove').forEach((register) => {
    const name = register.name.trim().toLowerCase();
    if (!name) errors[`registers.${register.key}.name`] = 'El nombre de la caja es obligatorio.';
    if (names.has(name)) errors[`registers.${register.key}.name`] = 'Usa un nombre diferente para cada caja.';
    names.add(name);
    if (!Number.isInteger(register.maxOpenSales) || register.maxOpenSales < 1) errors[`registers.${register.key}.maxOpenSales`] = 'Ingresa un entero mayor o igual a 1.';
  });
  if (!data.branch && !draft.registers.length) errors.registers = 'Agrega al menos una caja.';
  return errors;
};
